import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-238" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-239" data-mp="3dqpcv9l1uave">
        <div className="text-center mb-10" data-id="element-240" data-mp="3dpnwcpl9kw6a">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-ink uppercase" data-id="element-241" data-mp="38qulz619xlac">
            See Inside Workplace Safety <span className="text-forest" data-id="element-242" data-mp="2l2l42f76lhoh">Atlas</span>
          </h2>
          <p className="text-xl font-semibold text-forest max-w-3xl mx-auto" data-id="element-243" data-mp="2h0q3xe0zlaca">
            Professional Workplace Safety Training Material You Can Actually Teach From
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" data-id="element-244" data-mp="2q14p8ddyapfs">
          <div className="relative bg-mist border border-line rounded-2xl shadow-sm shadow-ink/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-245" data-mp="2pzjw8s7abek1">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-246" data-mp="19kp0syayn2v">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}`} data-id="element-247" data-mp="685ogq5qd2q9">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-248" data-mp="17r7psqiu36nz" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-forest/90 text-white flex items-center justify-center hover:bg-forest transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="View image larger" data-id="element-249" data-mp="2jcitdvz5feax">
              <Maximize2 size={18} data-id="element-250" data-mp="1vgwmm7ap2mgf" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-forest text-white border-2 border-gold shadow-lg flex items-center justify-center hover:bg-ink transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2" aria-label="Previous image" data-id="element-251" data-mp="1vir9vs9fuqno">
            <ChevronLeft size={24} data-id="element-252" data-mp="2dik7z8jrsbrm" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-forest text-white border-2 border-gold shadow-lg flex items-center justify-center hover:bg-ink transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2" aria-label="Next image" data-id="element-253" data-mp="2ii6kffrrxnzd">
            <ChevronRight size={24} data-id="element-254" data-mp="1yscznlnh894n" />
          </button>
        </div>

        <div className="max-w-3xl mx-auto text-center mt-6 min-h-[4.5rem]" aria-live="polite" data-id="element-255" data-mp="1kvzea1ftnnkw">
          <p className="text-base font-bold text-forest uppercase tracking-wide" data-id="element-256" data-mp="1psg4d16l0c0m">
            Image 0{index + 1} — {slide.title}
          </p>
          <p className="text-sm text-graphite/80 mt-1" data-id="element-257" data-mp="169sljq5uz70o">{slide.caption}</p>
        </div>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-258" data-mp="11azas5hyhu47">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${dotIndex === index ? 'w-8 bg-gold' : 'w-2.5 bg-ink/25 hover:bg-ink/40'}`} aria-label={`Go to image ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-259" data-mp="1b7c0ytealpu7" data-mpi={dotIndex} />)}
        </div>

        <div className="text-center mt-10 max-w-3xl mx-auto" data-id="element-260" data-mp="27xur23g6laqa">
          <p className="text-lg font-bold text-ink" data-id="element-261" data-mp="23w9i2t6rpgld">
            <span className="text-forest" data-id="element-262" data-mp="23tmzzzm93iow">REAL TRAINING MATERIAL. REAL TECHNICAL DIAGRAMS.</span>{' '}
            ONE COMPLETE VISUAL SYSTEM.
          </p>
          <p className="text-lg text-graphite/80 mt-3" data-id="element-263" data-mp="2fx7v4jpyhx1m">
            Inside the system, you&apos;ll find detailed workplace illustrations, industrial machinery
            diagrams, hazard-recognition activities, risk assessment examples, workplace scenarios and
            professional teaching resources.
          </p>
          <p className="text-lg font-semibold text-ink mt-3" data-id="element-264" data-mp="2tomfatvlo4a9">
            Everything organized to help you deliver clearer, more structured and visually engaging
            workplace safety training.
          </p>
        </div>
      </div>

      <AnimatePresence data-id="element-265" data-mp="33lqxkimbrfls">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.alt} onClick={() => setIsZoomed(false)} data-id="element-266" data-mp="38ldeb21d3dzc">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-light" aria-label="Close enlarged image" data-id="element-267" data-mp="3mqdnsp07hgkj">
              <X size={22} data-id="element-268" data-mp="5bgpd9ig1vqk" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-269" data-mp="5a5lzaswupzz" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}